import React, { useState, useEffect, useRef } from "react";
import {
  fetchSportsList,
  createSport,
  updateSport,
  deleteSport,
} from "../../../api/tournamentControlApi";
import { getCsrfToken } from "../../../../../public/api/authApi";
import {
  GlobalButton,
  GlobalTextField,
  GlobalTextAreaField,
  GlobalSelectField,
} from "../../../../../global/forms/GlobalFormFields";
import { GlobalTableActionButton } from "../../../../../global/GlobalTableActionButton";
import GlobalFormShell from "../../../../../global/forms/GlobalFormShell";
import { GlobalModal } from "../../../../../global/GlobalModal";
import GlobalDataTable from "../../../../../global/GlobalDataTable";
import GlobalBadge from "../../../../../global/GlobalBadge";
import GlobalSuccessFlashModal from "../../../../../global/GlobalSuccessFlashModal";

const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 2, label: "Inactive" },
];

const SportsMaster = ({ onDirectoryChange }) => {
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    sport_name: "",
    description: "",
    status_id: 1,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formErrorDetails, setFormErrorDetails] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const alertRef = useRef(null);
  const formSectionRef = useRef(null);

  const scrollToMasterForm = () => {
    requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  };

  useEffect(() => {
    loadSports();
  }, []);

  const loadSports = async () => {
    try {
      setLoading(true);
      const response = await fetchSportsList({ scope: "master" });
      if (response && response.success) {
        setSports(response.sports || []);
      } else {
        setSports(response || []);
      }
    } catch (err) {
      setError(err.message || "Failed to load sports");
    } finally {
      setLoading(false);
    }
  };

  const setFormField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrorDetails[name]) {
      setFormErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setFormError(null);
    setFormErrorDetails({});
    setSuccessMsg("");
    const wasEdit = isEditing;
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      if (wasEdit) {
        await updateSport(
          formData.id,
          {
            sport_name: formData.sport_name.trim(),
            status_id: Number(formData.status_id),
            description: formData.description.trim(),
          },
          csrfToken,
        );
      } else {
        await createSport(
          {
            sport_name: formData.sport_name.trim(),
            description: formData.description.trim(),
          },
          csrfToken,
        );
      }
      setFormData({ id: null, sport_name: "", description: "", status_id: 1 });
      setIsEditing(false);
      setSuccessMsg(
        wasEdit ? "Sport updated successfully." : "Sport added successfully.",
      );
      await loadSports();
      onDirectoryChange?.();
    } catch (err) {
      setFormError(err.message);
    }
  };

  const handleEdit = (sport) => {
    setSuccessMsg("");
    setFormData({
      id: sport.id,
      sport_name: sport.sport_name,
      description: sport.description ?? "",
      status_id:
        sport.status_id != null && sport.status_id !== ""
          ? Number(sport.status_id)
          : 1,
    });
    setIsEditing(true);
    scrollToMasterForm();
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deleteSport(itemToDelete.id, csrfToken);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      setSuccessMsg("Sport removed from the directory.");
      await loadSports();
      onDirectoryChange?.();
    } catch (err) {
      alert("Failed to remove: " + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setFormData({ id: null, sport_name: "", description: "", status_id: 1 });
    setFormError(null);
    setFormErrorDetails({});
    setIsEditing(false);
  };

  if (loading) return <p className="ub-loading">Loading sports…</p>;
  if (error) return <p className="ub-error">Error: {error}</p>;

  const dataList = Array.isArray(sports) ? sports : sports.sports || [];
  const ALERT_ID = "tournament-sport-master-alert";

  return (
    <div className="ub-master-section">
      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.25rem",
        }}
      >
        <h3 className="ub-section__subhead" style={{ margin: 0 }}>
          Sports master
        </h3>
        <GlobalButton
          variant="primary"
          size="sm"
          onClick={() => {
            setSuccessMsg("");
            setFormData({ id: null, sport_name: "", description: "", status_id: 1 });
            setIsEditing(false);
            setFormError(null);
            setFormErrorDetails({});
            scrollToMasterForm();
          }}
        >
          Add new sport
        </GlobalButton>
      </div>

      <div ref={formSectionRef} style={{ scrollMarginTop: "1rem" }}>
        <GlobalFormShell
          legend={isEditing ? "Edit sport" : "Add sport"}
          showAlert={!!formError}
          alertText={formError}
          alertId={ALERT_ID}
          formAlertRef={alertRef}
          actions={
            <>
              {isEditing ? (
                <GlobalButton variant="ghost" onClick={handleCancel}>
                  Cancel edit
                </GlobalButton>
              ) : null}
              <GlobalButton variant="primary" onClick={handleSubmit}>
                {isEditing ? "Save changes" : "Create sport"}
              </GlobalButton>
            </>
          }
        >
          <GlobalTextField
            id={isEditing ? "sport_name_edit" : "sport_name"}
            label="Sport name"
            value={formData.sport_name}
            onChange={(v) => setFormField("sport_name", v)}
            error={formErrorDetails.sport_name}
            baseAlertId={ALERT_ID}
            required
          />
          <GlobalTextField
            id={isEditing ? "sport_description_edit" : "sport_description"}
            label="Description"
            value={formData.description}
            onChange={(v) => setFormField("description", v)}
            error={formErrorDetails.description}
            baseAlertId={ALERT_ID}
            required
          />
          {isEditing ? (
            <GlobalSelectField
              id="sport_status_id"
              label="Status"
              value={String(formData.status_id)}
              onChange={(v) => setFormField("status_id", Number(v))}
              options={STATUS_OPTIONS}
              baseAlertId={ALERT_ID}
            />
          ) : null}
        </GlobalFormShell>
      </div>

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Sports master table"
        headers={[
          { key: "sport_name", label: "Sport name" },
          { key: "description", label: "Description" },
          { key: "status", label: "Status" },
          {
            key: "actions",
            label: "Actions",
            className: "ub-table__col-actions",
          },
        ]}
      >
        {dataList.map((sport) => (
          <tr key={sport.id}>
            <td style={{ fontWeight: "500" }}>{sport.sport_name}</td>
            <td
              style={{
                maxWidth: "280px",
                fontSize: "0.875rem",
                color: "#475569",
                verticalAlign: "top",
              }}
              title={sport.description || ""}
            >
              {sport.description
                ? sport.description.length > 120
                  ? `${sport.description.slice(0, 120)}…`
                  : sport.description
                : "—"}
            </td>
            <td>
              <GlobalBadge active={Number(sport.status_id) === 1} />
            </td>
            <td className="ub-table__col-actions">
              <div className="ub-table-actions">
                <GlobalTableActionButton action="edit" onClick={() => handleEdit(sport)} />
                <GlobalTableActionButton
                  action="delete"
                  onClick={() => {
                    setItemToDelete(sport);
                    setShowDeleteConfirm(true);
                  }}
                />
              </div>
            </td>
          </tr>
        ))}
        {dataList.length === 0 && (
          <tr>
            <td
              colSpan={4}
              style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}
            >
              No sports found
            </td>
          </tr>
        )}
      </GlobalDataTable>

      <GlobalModal
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        title="Confirm removal"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <GlobalButton
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isDeleting}
            >
              Remove
            </GlobalButton>
          </div>
        }
      >
        <p>
          Remove sport <b>{itemToDelete?.sport_name}</b> from the directory?
        </p>
      </GlobalModal>
    </div>
  );
};

export default SportsMaster;
